/**
 * Klingender Ton der gehoverten Note der Griffschrift-**Ausgabe**, für den Tonhöhen-Tooltip.
 *
 * Warum überhaupt ein Backend-Aufruf: in der Ausgabe ist die notierte Tonhöhe die
 * **Griffschrift-Staff-Position des Knopfes** und gerade nicht der erzeugte Ton – der klingende Ton
 * kommt ausschließlich aus `POST /api/note-grips` (siehe {@link ./notePitchLabel!notePitchLabel}).
 * Auf der Eingabe-Vorschau ist `enabled` aus und es läuft **kein einziger** Request.
 *
 * Zwei Eigenschaften tragen die Korrektheit:
 *
 * 1. **Gelesen wird ausschließlich aus dem Cache, nie direkt aus `response`.** Einziger Schreiber
 *    ist der `watch` unten, und der legt die Antwort unter `requestedNote` ab – der Note, für die
 *    sie erfragt wurde, nicht der inzwischen gehoverten. Ohne das hängte der Tooltip den Ton von
 *    Note A an Note B, sobald der Zeiger während des Requests weiterwandert (Requests brauchen
 *    100–500 ms, der Zeiger ist dann regelmäßig schon weiter).
 * 2. **Ein Cache-Treffer schaltet den Abruf ab**, nicht erst die Antwort: bei einem Treffer liefert
 *    `fetchTarget` `null`, damit entfällt neben dem Request auch das `DOMParser`-Reparse in
 *    `buildNoteGripsRequest`.
 *
 * Eine zusätzliche Hover-Intent-Verzögerung braucht es nicht: der 200-ms-`watchDebounced` von
 * {@link @/composables/useNoteGrips} ist bereits ein Verweil-Filter – wer 30 Noten in je
 * unter 200 ms überstreicht, feuert null Requests.
 */
import {computed, type ComputedRef, type MaybeRefOrGetter, shallowRef, toValue, watch} from 'vue';

import {useNoteGrips} from '@/composables/useNoteGrips';
import type {NoteInfo} from '@/composables/musicXmlDom';

import {createSoundingNoteCache} from './soundingNoteCache';

/** Geteilte leere Antwort – referenzstabil, damit der `computed` nie ein neues Array erzeugt
 *  (VUE_RULES Regel 2). Niemals mutieren. */
const NO_SOUNDING_NOTES: string[] = [];

export interface UseHoveredSoundingNote {
    /** Klingende Töne der gehoverten Note; leer, solange unbekannt. */
    soundingNotes: ComputedRef<string[]>;
    /** Läuft gerade ein Abruf für die gehoverte Note? (treibt den `…`-Platzhalter) */
    loading: ComputedRef<boolean>;
}

export function useHoveredSoundingNote(
    musicXml: MaybeRefOrGetter<string | null | undefined>,
    hoveredNote: MaybeRefOrGetter<NoteInfo | null | undefined>,
    enabled: MaybeRefOrGetter<boolean>,
): UseHoveredSoundingNote {
    const cache = createSoundingNoteCache();
    // Cache-Schreibzugriffe sind für Vue unsichtbar – dieser Zähler macht sie zur Abhängigkeit.
    const cacheVersion = shallowRef(0);

    /** Note, die abgerufen werden muss: nur bei eingeschalteter Ausgabe-Vorschau, klingender Note
     *  und Cache-Miss. */
    const fetchTarget = computed<NoteInfo | null>(() => {
        cacheVersion.value;
        if (!toValue(enabled)) return null;
        const xml = toValue(musicXml);
        const note = toValue(hoveredNote);
        if (!xml || !note || note.isRest) return null;
        return cache.get(xml, note) === undefined ? note : null;
    });

    const grips = useNoteGrips(
        () => toValue(musicXml) ?? undefined,
        () => fetchTarget.value,
    );

    watch(grips.response, (result) => {
        const note = grips.requestedNote.value;
        const xml = toValue(musicXml);
        if (!result || !note || !xml) return;
        cache.set(xml, note, result.sounding_notes);
        cacheVersion.value += 1;
    });

    const soundingNotes = computed<string[]>((oldValue) => {
        cacheVersion.value;
        if (!toValue(enabled)) return NO_SOUNDING_NOTES;
        const xml = toValue(musicXml);
        const note = toValue(hoveredNote);
        const hit = xml && note && !note.isRest ? cache.get(xml, note) : undefined;
        if (!hit) return NO_SOUNDING_NOTES;
        // oldValue-Pattern: identische Tonfolge behält die Referenz (VUE_RULES Regel 2).
        if (oldValue && oldValue.length === hit.length && oldValue.every((n, i) => n === hit[i])) return oldValue;
        return hit;
    });

    const loading = computed(() => grips.loading.value && fetchTarget.value !== null);

    return {soundingNotes, loading};
}
